import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "sourceType",
    "authType",
    "restSection",
    "s3Section",
    "gcsSection",
    "azureSection",
    "noAuthFields",
    "basicAuthFields",
    "bearerTokenFields",
    "apiKeyFields",
    "oauthFields",
    "authCodeOnlyFields",
    "grantTypeSelect",
    "passwordGrantFields",
    "redirectUri",
    "refreshTokenSection"
  ]

  connect() {
    this.updateSource()
    this.updateAuth()
    this.updateGrantType()
  }

  updateSource() {
    const type = this.sourceTypeTarget.value

    // Hide all sections first
    this.restSectionTargets.forEach(el => el.classList.add("hidden"))
    this.s3SectionTargets.forEach(el => el.classList.add("hidden"))
    this.gcsSectionTargets.forEach(el => el.classList.add("hidden"))
    this.azureSectionTargets.forEach(el => el.classList.add("hidden"))

    if (type === "rest_api") {
      this.restSectionTargets.forEach(el => el.classList.remove("hidden"))
      this.updateAuth() // Re-evaluate auth visibility
    } else if (type === "s3") {
      this.s3SectionTargets.forEach(el => el.classList.remove("hidden"))
    } else if (type === "gcs") {
      this.gcsSectionTargets.forEach(el => el.classList.remove("hidden"))
    } else if (type === "azure") {
      this.azureSectionTargets.forEach(el => el.classList.remove("hidden"))
    }
  }

  updateAuth() {
    // Only run this if we are in REST mode
    if (this.sourceTypeTarget.value !== "rest_api") return

    const authType = this.authTypeTarget.value

    this.basicAuthFieldsTargets.forEach(el => el.classList.add("hidden"))
    this.bearerTokenFieldsTargets.forEach(el => el.classList.add("hidden"))
    this.apiKeyFieldsTargets.forEach(el => el.classList.add("hidden"))
    if (this.hasOauthFieldsTarget) {
      this.oauthFieldsTargets.forEach(el => el.classList.add("hidden"))
    }

    if (authType === "Basic Auth") {
      this.basicAuthFieldsTargets.forEach(el => el.classList.remove("hidden"))
    } else if (authType === "Bearer Token") {
      this.bearerTokenFieldsTargets.forEach(el => el.classList.remove("hidden"))
    } else if (authType === "API Key") {
      this.apiKeyFieldsTargets.forEach(el => el.classList.remove("hidden"))
    } else if (authType === "OAuth 2.0" && this.hasOauthFieldsTarget) {
      this.oauthFieldsTargets.forEach(el => el.classList.remove("hidden"))
      this.updateGrantType()
    }
  }

  updateGrantType() {
    if (!this.hasGrantTypeSelectTarget || !this.hasAuthCodeOnlyFieldsTarget) return

    const grant = this.grantTypeSelectTarget.value
    this.passwordGrantFieldsTargets.forEach(el => el.classList.toggle("hidden", grant !== "password"))
    if (grant === "authorization_code") {
      this.authCodeOnlyFieldsTargets.forEach(el => el.classList.remove("hidden"))
    } else {
      this.authCodeOnlyFieldsTargets.forEach(el => el.classList.add("hidden"))
    }
  }

  copyRedirectUri(event) {
    event.preventDefault()
    if (!this.hasRedirectUriTarget) return
    const input = this.redirectUriTarget
    input.select()
    navigator.clipboard.writeText(input.value).then(() => {
      const btn = event.currentTarget
      const originalHtml = btn.innerHTML
      btn.innerHTML = '<i data-lucide="check" class="w-4 h-4"></i> Copied'
      if (window.lucide) window.lucide.createIcons()
      setTimeout(() => {
        btn.innerHTML = originalHtml
        if (window.lucide) window.lucide.createIcons()
      }, 1500)
    })
  }

  toggleRefreshToken(event) {
    event.preventDefault()
    this.refreshTokenSectionTarget.classList.toggle("hidden")
  }

  validate(event) {
    let isValid = true
    const type = this.sourceTypeTarget.value

    // Clear previous errors
    this.element.querySelectorAll(".border-red-500").forEach(el => el.classList.remove("border-red-500"))

    if (type === "rest_api") {
      // Validate Base URI
      const baseUri = this.element.querySelector('[name="api_credential[base_uri]"]')
      if (baseUri && !baseUri.value) {
        isValid = false
        this.highlightError(baseUri)
      }

      const authType = this.authTypeTarget.value
      if (authType === "Basic Auth") {
        isValid = this.validateInputs(this.basicAuthFieldsTargets) && isValid
      } else if (authType === "Bearer Token") {
        isValid = this.validateInputs(this.bearerTokenFieldsTargets) && isValid
      } else if (authType === "API Key") {
        isValid = this.validateInputs(this.apiKeyFieldsTargets) && isValid
      } else if (authType === "OAuth 2.0" && this.hasOauthFieldsTarget) {
        isValid = this.validateOauth() && isValid
      }

    } else if (type === "s3") {
      isValid = this.validateInputs(this.s3SectionTargets) && isValid
    } else if (type === "gcs") {
      isValid = this.validateInputs(this.gcsSectionTargets) && isValid
    } else if (type === "azure") {
      isValid = this.validateInputs(this.azureSectionTargets) && isValid
    }

    if (!isValid) {
      event.preventDefault()
      alert("Please fill in all required fields.")
    }
  }

  validateOauth() {
    const requiredKeys = ["oauth_grant_type", "oauth_client_id", "oauth_token_method", "oauth_token_url"]
    if (this.hasGrantTypeSelectTarget && this.grantTypeSelectTarget.value === "authorization_code") {
      requiredKeys.push("oauth_authorize_url")
    } else if (this.hasGrantTypeSelectTarget && this.grantTypeSelectTarget.value === "password") {
      requiredKeys.push("oauth_username", "oauth_password", "oauth_password_location")
    }
    let valid = true
    requiredKeys.forEach(key => {
      const input = this.element.querySelector(`[name="credentials[${key}][value]"]`)
      if (input && !input.value.trim()) {
        valid = false
        this.highlightError(input)
      }
    })
    return valid
  }

  validateInputs(targets) {
    let valid = true
    targets.forEach(section => {
      section.querySelectorAll('input:not([type="hidden"])').forEach(input => {
        // Skip inputs that are themselves inside a hidden container (e.g. authCodeOnlyFields)
        if (input.closest(".hidden")) return
        if (!input.value.trim()) {
          valid = false
          this.highlightError(input)
        }
      })
    })
    return valid
  }

  highlightError(input) {
    input.classList.add("border-red-500")
  }
}
